import { Button, Select, SelectItem, Alert } from "@heroui/react";
import { LuPlus, LuTrash2, LuPhone } from "react-icons/lu";
import { PhoneInput } from "@/components/ui";
import { useFetchData } from "@/hooks";

// Contact type options (fallback if API fails)
const FALLBACK_CONTACT_TYPES = [
    { value: 1, title: "WhatsApp" },
    { value: 2, title: "Telepon" },
    { value: 3, title: "Handphone" },
];

/**
 * Step 3: Contact Numbers
 * Dynamic form for adding multiple contact numbers
 */
const ContactStep = ({ contacts, setContacts }) => {
    // Fetch contact types from API
    const contactTypesData = useFetchData("select/contact-type");
    const contactTypes = contactTypesData.data || FALLBACK_CONTACT_TYPES;

    // Add new contact
    const handleAddContact = () => {
        setContacts([
            ...contacts,
            {
                contact_type_id: "",
                number: "",
            },
        ]);
    };

    // Update contact field
    const handleContactChange = (index, field, value) => {
        const updated = [...contacts];
        updated[index] = { ...updated[index], [field]: value };
        setContacts(updated);
    };

    // Remove contact
    const handleRemoveContact = (index) => {
        setContacts(contacts.filter((_, i) => i !== index));
    };

    return (
        <div className="flex flex-col gap-4">
            <Alert
                color="primary"
                variant="flat"
                icon={
                    <div className="flex items-center justify-center">
                        <LuPhone size={18} className="mx-auto" />
                    </div>
                }
                classNames={{
                    description: "text-xs",
                }}
                title="Nomor Kontak"
                description="Tambahkan nomor kontak gudang. Anda dapat menambahkan beberapa nomor dengan tipe berbeda."
            />

            {/* Contact List */}
            <div className="flex flex-col gap-4">
                {contacts.map((contact, index) => (
                    <div
                        key={index}
                        className="bg-default-50 border-default-200 flex flex-col gap-3 rounded-lg border p-4"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-default-600 text-sm font-medium">
                                Kontak {index + 1}
                            </span>
                            {contacts.length > 1 && (
                                <Button
                                    isIconOnly
                                    size="sm"
                                    variant="light"
                                    color="danger"
                                    onPress={() => handleRemoveContact(index)}
                                >
                                    <LuTrash2 size={16} />
                                </Button>
                            )}
                        </div>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            {/* Contact Type */}
                            <Select
                                label="Tipe Kontak"
                                placeholder="Pilih tipe"
                                variant="bordered"
                                selectedKeys={
                                    contact.contact_type_id
                                        ? [String(contact.contact_type_id)]
                                        : []
                                }
                                onSelectionChange={(keys) => {
                                    const selected = Array.from(keys)[0];
                                    handleContactChange(
                                        index,
                                        "contact_type_id",
                                        selected ? Number(selected) : "",
                                    );
                                }}
                                isLoading={contactTypesData.isLoading}
                            >
                                {contactTypes.map((type) => (
                                    <SelectItem
                                        key={String(type.value)}
                                        textValue={type.title}
                                    >
                                        {type.title}
                                    </SelectItem>
                                ))}
                            </Select>

                            {/* Phone Number */}
                            <PhoneInput
                                label="Nomor"
                                placeholder="8123456789"
                                value={contact.number}
                                onChange={(value) =>
                                    handleContactChange(index, "number", value)
                                }
                            />
                        </div>
                    </div>
                ))}
            </div>

            {/* Add More Button */}
            <Button
                variant="flat"
                color="primary"
                size="sm"
                startContent={<LuPlus size={18} />}
                onPress={handleAddContact}
                className="self-start"
            >
                Tambah Kontak Lain
            </Button>
        </div>
    );
};

export default ContactStep;
